import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, CheckCircle, AlertCircle, Package, Search, Filter, Printer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

export default function StockOpnameDetail() {
  const { opnameId } = useParams();
  const navigate = useNavigate();
  const [opname, setOpname] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, product, ingredient
  const [filterCounted, setFilterCounted] = useState('all'); // all, counted, uncounted
  const [editingItem, setEditingItem] = useState(null);
  const [physicalCount, setPhysicalCount] = useState('');
  const [itemNotes, setItemNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    fetchOpnameDetail();
  }, [opnameId]);

  const fetchOpnameDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/stock-opname/${opnameId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOpname(response.data.opname);
    } catch (error) {
      console.error('Error fetching opname detail:', error);
      toast.error('Gagal memuat data');
      navigate('/stock-opname');
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setPhysicalCount(item.physical_count !== null ? item.physical_count.toString() : '');
    setItemNotes(item.notes || '');
  };

  const handleSaveCount = async () => {
    if (!physicalCount || physicalCount === '') {
      toast.error('Masukkan jumlah stok fisik');
      return;
    }

    setSaving(true);
    
    // SAVE SCROLL POSITION before fetching new data
    const scrollPosition = window.scrollY || window.pageYOffset;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/stock-opname/${opnameId}/items/${editingItem.item_id}`,
        {
          physical_count: parseFloat(physicalCount),
          notes: itemNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Berhasil disimpan!');
      setEditingItem(null);
      setPhysicalCount('');
      setItemNotes('');
      
      // Fetch new data
      await fetchOpnameDetail();
      
      // RESTORE SCROLL POSITION after data is loaded
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
      
    } catch (error) {
      console.error('Error saving count:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    const uncounted = opname.items.filter(item => !item.counted).length;
    
    if (uncounted > 0) {
      toast.error(`Masih ada ${uncounted} item yang belum dihitung!`);
      return;
    }

    if (!window.confirm('Yakin ingin menyelesaikan stock opname ini? Stock akan disesuaikan secara otomatis.')) {
      return;
    }

    setCompleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/stock-opname/${opnameId}/complete`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Stock opname selesai! ${response.data.adjustments_applied} adjustments diterapkan.`);
      navigate('/stock-opname');
    } catch (error) {
      console.error('Error completing opname:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyelesaikan');
    } finally {
      setCompleting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getFilteredItems = () => {
    if (!opname || !opname.items) return [];

    let filtered = opname.items;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.item_type === filterType);
    }

    // Filter by counted status
    if (filterCounted === 'counted') {
      filtered = filtered.filter(item => item.counted);
    } else if (filterCounted === 'uncounted') {
      filtered = filtered.filter(item => !item.counted);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen billing-admin-shell flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  if (!opname) {
    return null;
  }

  const filteredItems = getFilteredItems();
  const isCompleted = opname.status === 'completed';
  const summary = opname.summary || {};
  
  // Pagination calculation
  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen billing-admin-shell p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          {/* Back Button - Separate Row */}
          <div className="mb-4 print:hidden">
            <button
              onClick={() => navigate('/stock-opname')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="h-5 w-5" />
              Kembali
            </button>
          </div>

          {/* Action Buttons - Separate Row */}
          <div className="flex flex-wrap gap-3 mb-4 print:hidden">
            <button
              onClick={handlePrint}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition text-sm"
            >
              <Printer className="h-4 w-4" />
              Print Laporan
            </button>

            <button
              onClick={() => window.open(`/stock-opname/${opnameId}/form`, '_blank')}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition text-sm"
            >
              <Printer className="h-4 w-4" />
              Print Form Counting
            </button>
            
            {!isCompleted && (
              <button
                onClick={handleComplete}
                disabled={completing || summary.items_counted !== summary.total_items}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50 text-sm"
              >
                <CheckCircle className="h-4 w-4" />
                {completing ? 'Memproses...' : 'Selesaikan & Apply'}
              </button>
            )}
          </div>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{opname.branch_name}</h1>
              <p className="text-gray-600 mb-3">Stock Opname - {new Date(opname.created_at).toLocaleDateString('id-ID')}</p>
              
              {/* Info Detail untuk Print */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-3">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Dibuat Oleh</p>
                  <p className="font-semibold text-gray-800">{opname.created_by_name}</p>
                  <p className="text-xs text-gray-500">{formatDate(opname.created_at)}</p>
                </div>
                
                {opname.started_at && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Dimulai</p>
                    <p className="text-xs text-gray-500">{formatDate(opname.started_at)}</p>
                  </div>
                )}
                
                {opname.completed_at && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Diselesaikan & Disetujui Oleh</p>
                    <p className="font-semibold text-gray-800">{opname.approved_by_name}</p>
                    <p className="text-xs text-gray-500">{formatDate(opname.completed_at)}</p>
                  </div>
                )}
              </div>
              
              {opname.notes && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Catatan</p>
                  <p className="text-sm text-gray-700">{opname.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Items</p>
              <p className="text-2xl font-bold text-blue-600">{summary.total_items || 0}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Sudah Dihitung</p>
              <p className="text-2xl font-bold text-green-600">{summary.items_counted || 0}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Ada Selisih</p>
              <p className="text-2xl font-bold text-orange-600">{summary.items_with_variance || 0}</p>
            </div>
            <div className={`p-4 rounded-lg ${summary.total_variance_value >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className="text-sm text-gray-600">Total Nilai Selisih</p>
              <p className={`text-lg font-bold ${summary.total_variance_value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.total_variance_value || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Filters - Hide on print */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-6 print:hidden">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari nama item..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">Semua Tipe</option>
                <option value="product">Product</option>
                <option value="ingredient">Ingredient</option>
              </select>

              <select
                value={filterCounted}
                onChange={(e) => {
                  setFilterCounted(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">Semua Status</option>
                <option value="uncounted">Belum Dihitung</option>
                <option value="counted">Sudah Dihitung</option>
              </select>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategori
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipe
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stok Sistem
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stok Fisik
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Selisih
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nilai Selisih
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {!isCompleted && (
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aksi
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      <Package className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                      Tidak ada item yang sesuai filter
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* Items for screen display (paginated) */}
                    {currentItems.map((item) => (
                      <tr key={item.item_id} className={`screen-only ${item.counted ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          {item.notes && (
                            <p className="text-xs text-orange-600 mt-1"> {item.notes}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-700 capitalize">{item.category}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          item.item_type === 'product' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {item.item_type === 'product' ? 'Product' : 'Ingredient'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {item.system_stock} {item.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                        {item.physical_count !== null ? `${item.physical_count} ${item.unit}` : '-'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-bold ${
                        item.variance === null ? 'text-gray-400' : 
                        item.variance > 0 ? 'text-green-600' : 
                        item.variance < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {item.variance !== null ? `${item.variance > 0 ? '+' : ''}${item.variance}` : '-'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-medium ${
                        item.variance_value === null ? 'text-gray-400' :
                        item.variance_value >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.variance_value !== null ? formatCurrency(item.variance_value) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {item.counted ? (
                          <span className="text-green-600 flex items-center justify-center gap-1">
                            <CheckCircle className="h-4 w-4" />
                            Sudah
                          </span>
                        ) : (
                          <span className="text-gray-400 flex items-center justify-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            Belum
                          </span>
                        )}
                      </td>
                      {!isCompleted && (
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handleEditItem(item)}
                            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-1 rounded text-sm transition"
                          >
                            {item.counted ? 'Edit' : 'Hitung'}
                          </button>
                        </td>
                      )}
                    </tr>
                    ))}
                    
                    {/* Items for print (all items) */}
                    {filteredItems.map((item) => (
                      <tr key={`print-${item.item_id}`} className={`print-only ${item.counted ? 'bg-green-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="font-medium text-gray-900">{item.name}</p>
                            {item.notes && (
                              <p className="text-xs text-orange-600 mt-1"> {item.notes}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-gray-700 capitalize">{item.category}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            item.item_type === 'product' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {item.item_type === 'product' ? 'Product' : 'Ingredient'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {item.system_stock} {item.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                          {item.physical_count !== null ? `${item.physical_count} ${item.unit}` : '-'}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-bold ${
                          item.variance === null ? 'text-gray-400' : 
                          item.variance > 0 ? 'text-green-600' : 
                          item.variance < 0 ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {item.variance !== null ? `${item.variance > 0 ? '+' : ''}${item.variance}` : '-'}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-medium ${
                          item.variance_value === null ? 'text-gray-400' :
                          item.variance_value >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {item.variance_value !== null ? formatCurrency(item.variance_value) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {item.counted ? (
                            <span className="text-green-600 flex items-center justify-center gap-1">
                              <CheckCircle className="h-4 w-4" />
                              Sudah
                            </span>
                          ) : (
                            <span className="text-gray-400 flex items-center justify-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              Belum
                            </span>
                          )}
                        </td>
                        {!isCompleted && (
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            -
                          </td>
                        )}
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between print:hidden">
              <div className="text-sm text-gray-700">
                Menampilkan <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">{Math.min(endIndex, totalItems)}</span> dari <span className="font-medium">{totalItems}</span> item
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show first, last, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-1 text-sm font-medium rounded-md ${
                            currentPage === pageNumber
                              ? 'bg-purple-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <span key={pageNumber} className="text-gray-500">...</span>;
                    }
                    return null;
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Input Stok Fisik</h2>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="font-semibold text-gray-800">{editingItem.name}</p>
              <p className="text-sm text-gray-600">{editingItem.category}</p>
              <p className="text-sm text-gray-600 mt-2">
                Stok Sistem: <span className="font-bold">{editingItem.system_stock} {editingItem.unit}</span>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stok Fisik (Hasil Hitung) *
                </label>
                <input
                  type="number"
                  value={physicalCount}
                  onChange={(e) => setPhysicalCount(e.target.value)}
                  placeholder={`Masukkan jumlah dalam ${editingItem.unit}`}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  step="0.01"
                  autoFocus
                />
              </div>

              {physicalCount && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-700">
                    Selisih: <span className={`font-bold ${
                      (parseFloat(physicalCount) - editingItem.system_stock) > 0 ? 'text-green-600' : 
                      (parseFloat(physicalCount) - editingItem.system_stock) < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {parseFloat(physicalCount) - editingItem.system_stock > 0 ? '+' : ''}
                      {(parseFloat(physicalCount) - editingItem.system_stock).toFixed(2)} {editingItem.unit}
                    </span>
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catatan (Optional)
                </label>
                <textarea
                  value={itemNotes}
                  onChange={(e) => setItemNotes(e.target.value)}
                  placeholder="Misal: 5 rusak, 3 expired"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="2"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setEditingItem(null);
                  setPhysicalCount('');
                  setItemNotes('');
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition"
                disabled={saving}
              >
                Batal
              </button>
              <button
                onClick={handleSaveCount}
                disabled={saving || !physicalCount}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print-specific styles */}
      <style>{`
        /* Screen: hide print-only rows, show screen-only rows */
        .print-only {
          display: none !important;
        }
        .screen-only {
          display: table-row !important;
        }
        
        @media print {
          /* Print: hide screen-only rows, show print-only rows */
          .screen-only {
            display: none !important;
          }
          .print-only {
            display: table-row !important;
          }
          
          /* Hide elements with print:hidden */
          .print\\:hidden {
            display: none !important;
          }
          
          /* Remove gradients and backgrounds */
          body {
            background: white !important;
          }
          
          .min-h-screen {
            background: white !important;
            min-height: auto !important;
          }
          
          .bg-gradient-to-br {
            background: white !important;
          }
          
          /* Remove shadows */
          .shadow-lg, .shadow-xl, .shadow-2xl {
            box-shadow: none !important;
          }
          
          /* Remove rounded corners */
          .rounded-lg, .rounded-full {
            border-radius: 0 !important;
          }
          
          /* Page breaks */
          .bg-white {
            page-break-inside: auto;
            margin-bottom: 10px;
          }
          
          table {
            page-break-inside: auto;
            width: 100%;
            border-collapse: collapse;
          }
          
          thead {
            display: table-header-group;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          tbody {
            display: table-row-group;
          }
          
          /* Table styling */
          th, td {
            border: 1px solid #000 !important;
            padding: 6px !important;
            font-size: 9px !important;
            text-align: left;
          }
          
          th {
            background-color: #e5e7eb !important;
            font-weight: bold !important;
          }
          
          /* Header styling */
          h1 {
            font-size: 20px !important;
            margin-bottom: 10px !important;
          }
          
          /* Summary cards */
          .grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
            margin-bottom: 15px;
          }
          
          /* Remove colored backgrounds, add borders */
          .bg-blue-50, .bg-green-50, .bg-orange-50, .bg-red-50,
          .bg-purple-50, .bg-gray-50 {
            background-color: white !important;
            border: 1px solid #000 !important;
            padding: 8px !important;
          }
          
          /* Keep text colors */
          .text-blue-600, .text-green-600, .text-orange-600, .text-red-600 {
            color: #000 !important;
            font-weight: bold !important;
          }
          
          /* Compact spacing */
          .p-6 {
            padding: 10px !important;
          }
          
          .mb-6 {
            margin-bottom: 10px !important;
          }
          
          /* Print title */
          @page {
            margin: 1cm;
          }
          
          /* Add header to first page */
          body::before {
            content: "LAPORAN STOCK OPNAME";
            display: block;
            text-align: center;
            font-size: 22px;
            font-weight: bold;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 3px solid #000;
          }
        }
      `}</style>
    </div>
  );
}
